/**
 * Google Calendar integration: the shapes the calendar API speaks, plus the small
 * formatting helpers every calendar surface shares.
 *
 * The backend owns Google — OAuth, tokens, free/busy and event writes all happen
 * server-side. The frontend only ever sees busy *ranges* (never event names or details)
 * and the events UnfoldWIN itself created.
 *
 * Times are the viewer's local time throughout, the same way Momentum groups steps.
 * Pure (no runtime imports) so it runs under `node --test`.
 */

export interface CalendarAccount {
  name: string;
  email: string;
}

export interface CalendarInfo {
  id: string;
  name: string;
  primary: boolean;
}

export interface CalendarConnection {
  provider: "google";
  /** `needs_reconnect`: Google ended access (revoked or expired); nothing can be added until it's renewed. */
  status: "connected" | "needs_reconnect";
  account: CalendarAccount;
  /** What the person allowed on Google's screen. Adding events is required to connect at all. */
  access: { events: boolean; freeBusy: boolean };
  /** Calendars the account can add events to. */
  calendars: CalendarInfo[];
  /** Used first when adding a step; follows the last one picked. */
  defaultCalendarId: string;
  /** The Settings switch. Free times are suggested only when this and `access.freeBusy` are both on. */
  suggestFreeTimes: boolean;
}

/** An ISO start/end pair. */
export interface TimeRange {
  start: string;
  end: string;
}

/** 0 = Sunday … 6 = Saturday, as Date#getDay. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export type Frequency = "once" | "daily" | "weekly" | "biweekly";
export type TimeOfDay = "morning" | "afternoon" | "evening" | "any";
/** What a rhythm does with a date that's busy at its time. */
export type IfBusy = "move" | "skip" | "keep";

export interface Rhythm {
  frequency: Exclude<Frequency, "once">;
  days: Weekday[];
  /** Start time, minutes after local midnight. */
  minutes: number;
  durationMin: number;
  /** First date, `yyyy-MM-dd` local. */
  startDate: string;
  /** Weeks it repeats for; null = ongoing (dates are checked and shown for the next 8). */
  weeks: number | null;
  ifBusy: IfBusy;
}

export interface ScheduledEvent extends TimeRange {
  id: string;
  /** Opens this event in Google Calendar. */
  htmlLink: string;
}

/** A Next Step's place on the calendar: one or more single events, or a rhythm. */
export interface StepSchedule {
  actionId: string;
  journalId: string;
  calendarId: string;
  kind: "once" | "rhythm";
  /** Every event UnfoldWIN created for the step, oldest first. */
  events: ScheduledEvent[];
  rhythm: Rhythm | null;
}

/** The day window suggestions and timelines cover: 7 AM to 9 PM. */
export const DAY_START_MIN = 7 * 60;
export const DAY_END_MIN = 21 * 60;
export const DURATIONS = [15, 30, 45, 60, 90] as const;
/** Weeks a rhythm is checked against, and shown for when it's ongoing. */
export const CHECK_WEEKS = 8;

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;
const MONTH_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const WEEKDAY_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

export function weekdayShort(day: number): string {
  return WEEKDAY_SHORT[day] ?? "";
}

export function weekdayLong(day: number): string {
  return WEEKDAY_LONG[day] ?? "";
}

export function monthLong(month: number): string {
  return MONTH_LONG[month] ?? "";
}

// ── Dates ────────────────────────────────────────────────────────────────────

/** Local midnight of the given date. */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes());
}

export function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** `yyyy-MM-dd` in local time. */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function parseDayKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** The local time `minutes` after midnight on `day`. */
export function atMinutes(day: Date, minutes: number): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, minutes);
}

export function minutesOf(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

// ── Formatting ───────────────────────────────────────────────────────────────

function clock(minutes: number): { h: number; m: string; pm: boolean } {
  const total = ((minutes % 1440) + 1440) % 1440;
  const h24 = Math.floor(total / 60);
  return { h: h24 % 12 === 0 ? 12 : h24 % 12, m: String(total % 60).padStart(2, "0"), pm: h24 >= 12 };
}

/** "8:00 AM" */
export function formatTime(minutes: number): string {
  const c = clock(minutes);
  return `${c.h}:${c.m} ${c.pm ? "PM" : "AM"}`;
}

/** "8:00", for compact labels where the timeline already says morning or evening. */
export function formatClock(minutes: number): string {
  const c = clock(minutes);
  return `${c.h}:${c.m}`;
}

/** "8:00 – 8:30 AM", or "11:30 AM – 12:15 PM" across noon. */
export function formatTimeRange(startMin: number, endMin: number): string {
  const a = clock(startMin);
  const b = clock(endMin);
  if (a.pm === b.pm) return `${a.h}:${a.m} – ${b.h}:${b.m} ${b.pm ? "PM" : "AM"}`;
  return `${formatTime(startMin)} – ${formatTime(endMin)}`;
}

/** "8:00-8:30", the timeline's block label. */
export function formatClockRange(startMin: number, endMin: number): string {
  return `${formatClock(startMin)}-${formatClock(endMin)}`;
}

/** "Wed, Oct 14" */
export function formatDay(d: Date): string {
  return `${WEEKDAY_SHORT[d.getDay()]}, ${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

/** "Oct 14" */
export function formatMonthDay(d: Date): string {
  return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
}

/** "Pacific Time" — the viewer's zone, as the footer names it. */
export function timeZoneLabel(at: Date = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZoneName: "longGeneric" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName");
    return part?.value ?? "Local time";
  } catch {
    return "Local time";
  }
}

export function partOfDay(minutes: number): Exclude<TimeOfDay, "any"> {
  if (minutes < 12 * 60) return "morning";
  if (minutes < 17 * 60) return "afternoon";
  return "evening";
}

/** "Tue & Thu", "Mon, Wed & Fri", "Every day", "Weekdays", "Every other Tue". */
export function rhythmDaysLabel(rhythm: Pick<Rhythm, "frequency" | "days">): string {
  const days = [...rhythm.days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)); // Monday first
  if (days.length === 7) return "Every day";
  if (days.length === 5 && days.every((d) => d >= 1 && d <= 5)) return "Weekdays";
  const names = days.map((d) => WEEKDAY_SHORT[d]);
  const list = names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
  return rhythm.frequency === "biweekly" ? `Every other ${list}` : list;
}

/** "Every Tue & Thu · 7:30 – 8:00 AM" */
export function rhythmLabel(rhythm: Rhythm): string {
  const days = rhythmDaysLabel(rhythm);
  const lead = days.startsWith("Every") || days === "Weekdays" ? days : `Every ${days}`;
  return `${lead} · ${formatTimeRange(rhythm.minutes, rhythm.minutes + rhythm.durationMin)}`;
}

// ── Schedules ────────────────────────────────────────────────────────────────

/** The next event still to come (or the latest one, once they've all passed). */
export function nextEvent(schedule: StepSchedule, now: Date = new Date()): ScheduledEvent | null {
  const sorted = [...schedule.events].sort((a, b) => a.start.localeCompare(b.start));
  return sorted.find((e) => new Date(e.end) > now) ?? sorted[sorted.length - 1] ?? null;
}

export function upcomingEvents(schedule: StepSchedule, now: Date = new Date()): ScheduledEvent[] {
  return schedule.events.filter((e) => new Date(e.start) > now);
}

/** The chip a scheduled step wears: "Wed, Oct 14 · 8:00 AM" or "Tue & Thu · 7:30 AM · until Dec 10". */
export function scheduleChipLabel(schedule: StepSchedule, now: Date = new Date()): string {
  if (schedule.kind === "rhythm" && schedule.rhythm) {
    const r = schedule.rhythm;
    const base = `${rhythmDaysLabel(r)} · ${formatTime(r.minutes)}`;
    if (r.weeks === null) return `${base} · ongoing`;
    const last = [...schedule.events].sort((a, b) => b.start.localeCompare(a.start))[0];
    return last ? `${base} · until ${formatMonthDay(new Date(last.start))}` : base;
  }
  const next = nextEvent(schedule, now);
  if (!next) return "On your calendar";
  const start = new Date(next.start);
  return `${formatDay(start)} · ${formatTime(minutesOf(start))}`;
}
